import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/formatters';
import { Plus, Minus, Search, ShoppingCart, Wheat, ArrowLeft, ChevronRight, Clock, Check, Copy, Banknote, QrCode, CreditCard } from 'lucide-react';

function calcularPrazo(cart, produtos) {
  const ordem = { '24h': 1, '48h': 2, '72h': 3 };
  let maior = '48h';
  Object.keys(cart).forEach(pid => {
    const p = produtos.find(x => x.id === pid);
    const prazo = p?.prazo_entrega || '48h';
    if ((ordem[prazo] || 2) > (ordem[maior] || 2)) maior = prazo;
  });
  return maior;
}

export default function PedidoNovo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [categoriaAtiva, setCategoriaAtiva] = useState('');
  const [cart, setCart] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [baseNumero, setBaseNumero] = useState('');
  const [descontoPct, setDescontoPct] = useState(0);
  // Modal de quantidade
  const [qtyModal, setQtyModal] = useState(null); // { produto, isEdit }
  const [qtyModalVal, setQtyModalVal] = useState('');
  const qtyInputRef = useRef(null);
  // Modal de pagamento
  const [showPagamento, setShowPagamento] = useState(false);
  const [formaPagamento, setFormaPagamento] = useState('avista');
  const [metodoPagamento, setMetodoPagamento] = useState('dinheiro');
  const [pixConfirmado, setPixConfirmado] = useState(null);
  const [pixCopiado, setPixCopiado] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const baseId = searchParams.get('base');

      // Carregar produtos e categorias independentemente para maior resiliência
      const [prodRes, catRes, perfilRes] = await Promise.all([
        api.get('/produtos?is_ativo=true'),
        api.get('/categorias').catch(() => ({ data: [] })),
        api.get('/clientes/me').catch(() => ({ data: {} })),
      ]);
      setProdutos(prodRes.data || []);
      setCategorias(catRes.data || []);
      setDescontoPct(parseFloat(perfilRes.data?.desconto_percentual || 0));

      // Carregar pedido base para duplicação (separado para não bloquear o fluxo)
      if (baseId) {
        try {
          const baseRes = await api.get(`/pedidos/${baseId}`);
          const baseCart = {};
          baseRes.data.itens?.forEach(i => { baseCart[i.produto_id] = i.quantidade; });
          setCart(baseCart);
          setBaseNumero(baseRes.data.numero || '');
          setObservacoes(baseRes.data.observacoes || '');
          setStep(2);
        } catch { /* ignora se pedido base não existe */ }
      }
    } catch {
      setError('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => { loadData(); }, [loadData]);

  // Retorna o mínimo do produto ou, se não tiver, o mínimo da categoria
  const getMinimo = (produto) => {
    if (produto.pedido_minimo > 1) return produto.pedido_minimo;
    const cat = categorias.find(c => c.id === produto.categoria_id);
    return cat?.pedido_minimo > 0 ? cat.pedido_minimo : 1;
  };

  const addToCart = (p) => {
    const min = getMinimo(p);
    setCart(c => ({ ...c, [p.id]: Math.max((c[p.id] || 0) + 1, min) }));
  };

  const removeFromCart = (pid) => {
    setCart(c => {
      const n = { ...c };
      if ((n[pid] || 0) <= 1) { delete n[pid]; } else { n[pid]--; }
      return n;
    });
  };

  const openQtyModal = (produto, isEdit = false) => {
    setQtyModal({ produto, isEdit });
    setQtyModalVal(isEdit ? String(cart[produto.id] || 1) : '');
    setTimeout(() => qtyInputRef.current?.focus(), 80);
  };

  const confirmQtyModal = () => {
    if (!qtyModal) return;
    const val = parseInt(qtyModalVal, 10);
    const min = getMinimo(qtyModal.produto);
    if (!isNaN(val) && val > 0) {
      setCart(c => ({ ...c, [qtyModal.produto.id]: Math.max(val, min) }));
    } else if (!isNaN(val) && val <= 0) {
      setCart(c => { const n = { ...c }; delete n[qtyModal.produto.id]; return n; });
    }
    setQtyModal(null);
    setQtyModalVal('');
  };

  const closeQtyModal = () => { setQtyModal(null); setQtyModalVal(''); };

  const cartCount = () => Object.values(cart).reduce((a, b) => a + b, 0);

  const cartSubtotal = () => Object.entries(cart).reduce((acc, [pid, qty]) => {
    const p = produtos.find(x => x.id === pid);
    return acc + (p?.preco_unitario || 0) * qty;
  }, 0);

  const cartDesconto = () => parseFloat((cartSubtotal() * descontoPct / 100).toFixed(2));
  const cartTotal = () => parseFloat((cartSubtotal() - cartDesconto()).toFixed(2));

  const prazoCalculado = calcularPrazo(cart, produtos);

  const handleSubmit = async () => {
    if (cartCount() === 0) { setError('Adicione pelo menos 1 item'); return; }
    setSubmitting(true);
    setError('');
    try {
      const itens = Object.entries(cart).map(([produto_id, quantidade]) => ({ produto_id, quantidade }));
      const usaPix = metodoPagamento === 'pix';
      let chavePix = '';
      if (usaPix) {
        const empresaRes = await api.get('/empresa');
        const dadosRec = empresaRes.data?.dados_recebimento || [];
        chavePix = dadosRec.length > 0 ? dadosRec[0].chave_pix : '';
      }
      const formaPagamentoLabel = `${formaPagamento === 'avista' ? 'À vista' : 'À prazo'} - ${metodoPagamento === 'dinheiro' ? 'Dinheiro' : metodoPagamento === 'pix' ? 'Pix' : 'Boleto'}`;
      const res = await api.post('/pedidos', {
        itens,
        prazo: prazoCalculado,
        observacoes,
        forma_pagamento: formaPagamentoLabel,
        chave_pix_usada: chavePix,
      });
      if (usaPix && chavePix) {
        setPixConfirmado(chavePix);
        setShowPagamento(false);
      } else {
        navigate(`/pedidos/ok/${res.data.id}`);
      }
    } catch (err) {
      setError(err.userMessage || 'Erro ao enviar pedido');
      setSubmitting(false);
    }
  };

  const filteredProdutos = produtos.filter(p => {
    const matchCat = !categoriaAtiva || p.categoria_id === categoriaAtiva;
    const matchSearch = !search ||
      p.descricao?.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const itensCarrinho = Object.entries(cart)
    .map(([pid, qty]) => ({ produto: produtos.find(p => p.id === pid), qty }))
    .filter(x => x.produto);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-brand-text-muted text-sm font-body">Carregando...</p>
    </div>
  );

  /* ── STEP 2: REVISÃO ── */
  if (step === 2) {
    return (
      <>
      <div className="pb-6" data-testid="pedido-revisao">
        <div className="bg-brand-primary px-4 pt-10 pb-5 text-white">
          <button onClick={() => setStep(1)} className="flex items-center gap-1 mb-3 opacity-80 hover:opacity-100">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-body">Voltar</span>
          </button>
          <h1 className="font-heading text-2xl font-bold">
            {baseNumero ? `Duplicar Pedido #${baseNumero}` : 'Revisar Pedido'}
          </h1>
          <p className="font-body text-sm opacity-80 mt-0.5">{user?.nome}</p>
        </div>

        <div className="px-4 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="pedido-error">
              {error}
            </div>
          )}

          <div className="card-mobile flex items-center gap-3">
            <Clock className="w-5 h-5 text-brand-primary flex-shrink-0" />
            <div>
              <p className="text-xs text-brand-text-muted font-body">Prazo de entrega (calculado automaticamente)</p>
              <p className="text-base font-dashboard font-bold text-brand-primary">{prazoCalculado}</p>
            </div>
          </div>

          <div className="card-mobile">
            <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide mb-3">
              Itens ({cartCount()})
            </p>
            <div className="space-y-3">
              {itensCarrinho.map(({ produto: p, qty }) => (
                <div key={p.id} className="flex items-center gap-3 py-2 border-b border-brand-border last:border-0" data-testid="review-item">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-body font-medium text-brand-text">{p.descricao}</p>
                    <p className="text-xs text-brand-text-muted font-body">
                      {p.preco_unitario > 0
                        ? `${formatCurrency(p.preco_unitario)} / ${p.unidade_medida}`
                        : p.unidade_medida}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => removeFromCart(p.id)} className="w-7 h-7 rounded-full bg-brand-accent-light flex items-center justify-center">
                      <Minus className="w-3 h-3 text-brand-dark" />
                    </button>
                    <span
                      className="w-8 text-center font-dashboard font-bold text-sm cursor-pointer hover:bg-brand-accent-light rounded-md py-0.5"
                      onClick={() => openQtyModal(p, true)}
                      data-testid="review-qty"
                    >{qty}</span>
                    <button onClick={() => addToCart(p)} className="w-7 h-7 rounded-full bg-brand-primary flex items-center justify-center">
                      <Plus className="w-3 h-3 text-white" />
                    </button>
                  </div>
                  <div className="text-right flex-shrink-0 w-16">
                    {p.preco_unitario > 0 ? (
                      <span className="text-sm font-semibold text-brand-primary">
                        {formatCurrency(p.preco_unitario * qty)}
                      </span>
                    ) : (
                      <span className="text-xs text-brand-text-muted">{qty}x</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {cartSubtotal() > 0 && (
              <div className="mt-4 pt-3 border-t border-brand-border space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="font-body text-sm text-brand-text-muted">Subtotal</span>
                  <span className="font-body text-sm text-brand-text-muted">{formatCurrency(cartSubtotal())}</span>
                </div>
                {descontoPct > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="font-body text-sm text-green-600">Desconto ({descontoPct}%)</span>
                    <span className="font-body text-sm text-green-600">- {formatCurrency(cartDesconto())}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1 border-t border-brand-border">
                  <span className="font-dashboard font-bold text-sm text-brand-text">Total do Pedido</span>
                  <span className="font-dashboard font-bold text-lg text-brand-primary">{formatCurrency(cartTotal())}</span>
                </div>
              </div>
            )}
          </div>

          <div className="card-mobile">
            <label className="block text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide mb-2">
              Observações (opcional)
            </label>
            <textarea
              className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm font-body resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              rows={3}
              placeholder="Ex: sem gergelim, entregar no portão dos fundos..."
              value={observacoes}
              onChange={e => setObservacoes(e.target.value)}
              data-testid="observacoes-input"
            />
          </div>

          <button
            onClick={() => setShowPagamento(true)}
            disabled={submitting || cartCount() === 0}
            className="w-full py-4 rounded-2xl btn-primary font-dashboard font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="finalizar-pedido-btn"
          >
            <ShoppingCart className="w-5 h-5" />
            {submitting ? 'Enviando...' : 'Forma de Pagamento'}
          </button>
        </div>
      </div>

      {/* Modal Forma de Pagamento */}
      {showPagamento && (
        <div className="fixed inset-0 bg-black/60 flex items-end z-50" onClick={() => setShowPagamento(false)}>
          <div className="w-full max-w-[430px] mx-auto bg-white rounded-t-2xl p-5 pb-8" onClick={e => e.stopPropagation()} data-testid="pagamento-modal">
            <h3 className="font-dashboard font-bold text-brand-text text-base mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-brand-primary" /> Forma de Pagamento
            </h3>

            {/* À vista / À prazo */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[{ v: 'avista', l: 'À vista' }, { v: 'aprazo', l: 'À prazo' }].map(({ v, l }) => (
                <button key={v} onClick={() => { setFormaPagamento(v); setMetodoPagamento(v === 'avista' ? 'dinheiro' : 'pix'); }}
                  className={`py-2.5 rounded-xl border-2 text-sm font-dashboard font-semibold transition-colors ${formaPagamento === v ? 'border-brand-primary bg-brand-accent-light text-brand-primary' : 'border-brand-border text-brand-text-muted'}`}
                  data-testid={`tipo-${v}`}
                >{l}</button>
              ))}
            </div>

            {/* Métodos */}
            <div className="space-y-2 mb-5">
              {formaPagamento === 'avista' ? (
                <>
                  <button onClick={() => setMetodoPagamento('dinheiro')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors ${metodoPagamento === 'dinheiro' ? 'border-brand-primary bg-brand-accent-light' : 'border-brand-border'}`}
                    data-testid="metodo-dinheiro">
                    <Banknote className={`w-5 h-5 ${metodoPagamento === 'dinheiro' ? 'text-brand-primary' : 'text-brand-text-muted'}`} />
                    <span className={`font-body font-medium text-sm ${metodoPagamento === 'dinheiro' ? 'text-brand-primary' : 'text-brand-text'}`}>Dinheiro</span>
                    {metodoPagamento === 'dinheiro' && <Check className="w-4 h-4 text-brand-primary ml-auto" />}
                  </button>
                  <button onClick={() => setMetodoPagamento('pix')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors ${metodoPagamento === 'pix' ? 'border-brand-primary bg-brand-accent-light' : 'border-brand-border'}`}
                    data-testid="metodo-pix-avista">
                    <QrCode className={`w-5 h-5 ${metodoPagamento === 'pix' ? 'text-brand-primary' : 'text-brand-text-muted'}`} />
                    <span className={`font-body font-medium text-sm ${metodoPagamento === 'pix' ? 'text-brand-primary' : 'text-brand-text'}`}>Pix</span>
                    {metodoPagamento === 'pix' && <Check className="w-4 h-4 text-brand-primary ml-auto" />}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setMetodoPagamento('pix')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors ${metodoPagamento === 'pix' ? 'border-brand-primary bg-brand-accent-light' : 'border-brand-border'}`}
                    data-testid="metodo-pix-aprazo">
                    <QrCode className={`w-5 h-5 ${metodoPagamento === 'pix' ? 'text-brand-primary' : 'text-brand-text-muted'}`} />
                    <span className={`font-body font-medium text-sm ${metodoPagamento === 'pix' ? 'text-brand-primary' : 'text-brand-text'}`}>Pix</span>
                    {metodoPagamento === 'pix' && <Check className="w-4 h-4 text-brand-primary ml-auto" />}
                  </button>
                  <button onClick={() => setMetodoPagamento('boleto')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors ${metodoPagamento === 'boleto' ? 'border-brand-primary bg-brand-accent-light' : 'border-brand-border'}`}
                    data-testid="metodo-boleto">
                    <CreditCard className={`w-5 h-5 ${metodoPagamento === 'boleto' ? 'text-brand-primary' : 'text-brand-text-muted'}`} />
                    <span className={`font-body font-medium text-sm ${metodoPagamento === 'boleto' ? 'text-brand-primary' : 'text-brand-text'}`}>Boleto</span>
                    {metodoPagamento === 'boleto' && <Check className="w-4 h-4 text-brand-primary ml-auto" />}
                  </button>
                </>
              )}
            </div>

            <button onClick={handleSubmit} disabled={submitting}
              className="w-full py-3.5 rounded-xl bg-brand-primary text-white font-dashboard font-bold text-sm disabled:opacity-60"
              data-testid="confirmar-pagamento-btn">
              {submitting ? 'Enviando...' : 'Confirmar Pedido'}
            </button>
          </div>
        </div>
      )}

      {/* Tela Pix após confirmação */}
      {pixConfirmado && (
        <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center p-6" data-testid="pix-screen">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <QrCode className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="font-dashboard font-bold text-xl text-brand-text mb-1">Pedido Confirmado!</h2>
          <p className="text-sm text-brand-text-muted font-body mb-6 text-center">Realize o pagamento via Pix para confirmar seu pedido.</p>
          <div className="w-full bg-brand-bg rounded-2xl p-4 mb-4 text-center">
            <p className="text-xs text-brand-text-muted font-body mb-1">Chave Pix</p>
            <p className="font-dashboard font-bold text-brand-text text-base break-all">{pixConfirmado}</p>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(pixConfirmado); setPixCopiado(true); setTimeout(() => setPixCopiado(false), 2000); }}
            className="w-full py-3.5 rounded-xl bg-brand-primary text-white font-dashboard font-bold text-sm flex items-center justify-center gap-2 mb-3"
            data-testid="copiar-pix-btn">
            <Copy className="w-4 h-4" /> {pixCopiado ? 'Copiado!' : 'Copiar Chave Pix'}
          </button>
          <button onClick={() => navigate('/home')} className="text-sm text-brand-text-muted font-body underline">
            Ir para o início
          </button>
        </div>
      )}
      </>
    );
  }

  /* ── STEP 1: SELEÇÃO DE PRODUTOS ── */
  return (
    <div className="pb-4" data-testid="pedido-novo-page">
      <div className="bg-brand-primary px-4 pt-10 pb-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <Wheat className="w-5 h-5" strokeWidth={1.5} />
          <img src="/logo-fatima.jpg" alt="Fátima Guterres" className="h-6 w-auto rounded object-cover" />
        </div>
        <h1 className="font-heading text-3xl font-bold">Novo Pedido</h1>
        <p className="font-body text-sm opacity-80 mt-1">{user?.nome}</p>
      </div>

      <div className="px-4 py-4 space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="pedido-error">
            {error}
          </div>
        )}

        {/* Chips de categoria — scroll horizontal com imagens */}
        {categorias.length > 0 && (
          <div
            className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            data-testid="categoria-chips"
          >
            <button
              onClick={() => setCategoriaAtiva('')}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-dashboard font-semibold border transition-all whitespace-nowrap ${
                categoriaAtiva === ''
                  ? 'bg-brand-primary text-white border-brand-primary shadow-sm'
                  : 'bg-white text-brand-text-muted border-brand-border'
              }`}
              data-testid="chip-todos"
            >
              Todos
            </button>
            {categorias.map(cat => (
              <button
                key={cat.id}
                onClick={() => setCategoriaAtiva(prev => prev === cat.id ? '' : cat.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-dashboard font-semibold border transition-all whitespace-nowrap ${
                  categoriaAtiva === cat.id
                    ? 'bg-brand-primary text-white border-brand-primary shadow-sm'
                    : 'bg-white text-brand-text-muted border-brand-border'
                }`}
                data-testid={`chip-cat-${cat.id}`}
              >
                {cat.imagem_url ? (
                  <img
                    src={cat.imagem_url}
                    alt={cat.nome}
                    className="w-4 h-4 rounded-full object-cover flex-shrink-0"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : null}
                {cat.nome}
              </button>
            ))}
          </div>
        )}

        {/* Campo de busca */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted" />
          <input
            className="input-mobile pl-10"
            placeholder="Buscar produto..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="product-search"
          />
        </div>

        {/* Contador */}
        {(categoriaAtiva || search) && (
          <p className="text-xs text-brand-text-muted font-body px-1">
            {filteredProdutos.length} produto(s)
            {categoriaAtiva && ` em "${categorias.find(c => c.id === categoriaAtiva)?.nome}"`}
          </p>
        )}

        {/* Lista de produtos */}
        <div className="space-y-2">
          {filteredProdutos.length === 0 && (
            <p className="text-center text-brand-text-muted text-sm py-8 font-body">Nenhum produto encontrado</p>
          )}
          {filteredProdutos.map(p => (
            <div key={p.id} className="card-mobile flex items-center gap-3" data-testid="product-item">
              {p.imagem_url ? (
                <img src={p.imagem_url} alt={p.descricao} className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-brand-border" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
              ) : null}
              <div className={`w-12 h-12 rounded-xl bg-brand-accent-light flex items-center justify-center flex-shrink-0 ${p.imagem_url ? 'hidden' : 'flex'}`}>
                <Wheat className="w-6 h-6 text-brand-primary/40" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body font-medium text-brand-text text-sm truncate">{p.descricao}</p>
                <p className="text-xs text-brand-text-muted font-body">{p.sku} · {p.unidade_medida}</p>
                <p className="text-xs font-semibold text-brand-primary mt-0.5">
                  {p.preco_unitario > 0 ? formatCurrency(p.preco_unitario) : <span className="text-brand-text-muted font-normal">Preço sob consulta</span>}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                {cart[p.id] > 0 ? (
                  <>
                    <button onClick={() => removeFromCart(p.id)} className="w-8 h-8 rounded-full bg-brand-accent-light flex items-center justify-center" data-testid="decrease-qty">
                      <Minus className="w-4 h-4 text-brand-dark" />
                    </button>
                    <span
                      className="w-8 text-center font-dashboard font-bold text-brand-text text-sm cursor-pointer hover:bg-brand-accent-light rounded-md py-0.5"
                      onClick={() => openQtyModal(p, true)}
                      data-testid="item-qty"
                    >{cart[p.id]}</span>
                    <button onClick={() => addToCart(p)} className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center" data-testid="increase-qty">
                      <Plus className="w-4 h-4 text-white" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => openQtyModal(p, false)}
                    className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center shadow-sm"
                    data-testid="add-to-cart"
                  >
                    <Plus className="w-5 h-5 text-white" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de quantidade */}
      {qtyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={closeQtyModal} data-testid="qty-modal-overlay">
          <div className="w-full max-w-[430px] mx-auto bg-white rounded-t-2xl p-5 pb-8" onClick={e => e.stopPropagation()}>
            <p className="text-xs text-brand-text-muted font-body mb-1 truncate">{qtyModal.produto.descricao}</p>
            {qtyModal.produto.preco_unitario > 0 && (
              <p className="text-sm font-semibold text-brand-primary mb-3">{formatCurrency(qtyModal.produto.preco_unitario)} / {qtyModal.produto.unidade_medida}</p>
            )}
            <label className="block text-sm font-dashboard font-semibold text-brand-text mb-2">
              {qtyModal.isEdit ? 'Alterar quantidade' : 'Quantidade'}
            </label>
            <input
              ref={qtyInputRef}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min="1"
              placeholder="Digite a quantidade..."
              className="w-full rounded-xl border-2 border-brand-primary px-4 py-3 text-xl font-bold text-center text-brand-text outline-none focus:ring-2 focus:ring-brand-primary/30"
              value={qtyModalVal}
              onChange={e => setQtyModalVal(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmQtyModal()}
              data-testid="qty-modal-input"
            />
            <div className="flex gap-3 mt-4">
              <button onClick={closeQtyModal} className="flex-1 py-3 rounded-xl border border-brand-border text-brand-text font-dashboard font-medium text-sm" data-testid="qty-modal-cancel">
                Cancelar
              </button>
              <button onClick={confirmQtyModal} className="flex-1 py-3 rounded-xl bg-brand-primary text-white font-dashboard font-bold text-sm" data-testid="qty-modal-confirm">
                {qtyModal.isEdit ? 'Atualizar' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra flutuante do carrinho */}
      {cartCount() > 0 && (
        <div className="fixed bottom-20 left-0 right-0 px-4 max-w-[430px] mx-auto" data-testid="cart-summary">
          <button
            onClick={() => { setError(''); setStep(2); }}
            className="w-full py-4 rounded-2xl bg-brand-dark text-white flex items-center justify-between px-5 shadow-xl"
            data-testid="revisar-pedido-btn"
          >
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              <span className="font-dashboard font-semibold text-sm">{cartCount()} item(ns)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-dashboard font-bold text-sm">
                {cartTotal() > 0 ? formatCurrency(cartTotal()) : 'Revisar'}
              </span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
